// Admin — Phone Numbers.
//
// Lists every retailer alongside the Twilio "click-to-connect" platform number
// allocated to them, shows the shared number pool and how heavily each number
// is used, and lets an admin allocate / re-allocate / auto-assign a number.
//
// Backend (already existed — this page is the missing UI):
//   GET  /api/calls/numbers            → { pool: [{ number, masked, assignedTo:[{retailerId,label}] }] }
//   POST /api/calls/assign             → body { retailerId, platformNumber? } (omit number = auto-allocate)
//   GET  /api/admin/retailers          → [{ id, businessName, twilioNumber, ... }]
//
// The pool is configured via the CLICK2CONNECT_NUMBER_POOL env var. Assignments
// live in data/calls.json keyed by the retailer's id/slug — the same id the
// admin retailers list returns, so the two join cleanly.

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";
import "./styles.css";

interface ServerRetailer {
  id?: string;
  businessName?: string;
  email?: string;
  status?: string;
}

interface PoolNumber {
  number: string;
  masked?: string;
  assignedTo?: Array<{ retailerId: string; label?: string }>;
}

interface NumbersResponse {
  ok?: boolean;
  pool?: PoolNumber[];
}

interface RetailerRow {
  id: string;
  businessName: string;
  status: string;
  /** The platform number currently allocated to this retailer, or "" if none. */
  number: string;
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

const phoneNumbersPage: Page = {
  name: "phone-numbers",
  label: "Phone Numbers",
  icon: "📞",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let pool: PoolNumber[] = [];
    let retailers: RetailerRow[] = [];
    let loading = true;
    let error: string | null = null;
    let search = "";
    let busyId: string | null = null;

    const root = document.createElement("section");
    root.className = "atn-page";
    root.innerHTML = `
      <header class="atn-header">
        <h1>Phone Numbers</h1>
        <p>Allocate Twilio click-to-connect numbers to retailers and see how the shared pool is used.</p>
      </header>
      <div class="atn-pool" data-role="pool" aria-live="polite"></div>
      <div class="atn-toolbar">
        <input class="adm-input atn-search" type="search" data-role="search"
               placeholder="Search retailers by name…" aria-label="Search retailers" />
        <span class="atn-spacer"></span>
        <button class="adm-btn adm-btn-ghost" type="button" data-role="refresh">Refresh</button>
      </div>
      <div class="atn-list" data-role="list" aria-live="polite"></div>
    `;
    container.appendChild(root);

    const poolEl = root.querySelector<HTMLDivElement>("[data-role='pool']")!;
    const listEl = root.querySelector<HTMLDivElement>("[data-role='list']")!;
    const searchEl = root.querySelector<HTMLInputElement>("[data-role='search']")!;
    const refreshBtn = root.querySelector<HTMLButtonElement>("[data-role='refresh']")!;

    function poolOptions(currentNumber: string): string {
      // "Auto-allocate" + every pool number; mark the current one as selected.
      const opts = [`<option value="">Auto-allocate (least used)</option>`];
      for (const p of pool) {
        const used = (p.assignedTo || []).length;
        const sel = p.number === currentNumber ? " selected" : "";
        opts.push(
          `<option value="${esc(p.number)}"${sel}>${esc(p.number)}${used ? ` · ${used} assigned` : " · free"}</option>`,
        );
      }
      return opts.join("");
    }

    function renderPool(): void {
      if (loading) {
        poolEl.innerHTML = `<div class="adm-loading">Loading pool…</div>`;
        return;
      }
      if (pool.length === 0) {
        poolEl.innerHTML = `<div class="atn-pool-empty">No numbers configured in the pool (CLICK2CONNECT_NUMBER_POOL is empty).</div>`;
        return;
      }
      poolEl.innerHTML = `
        <div class="atn-pool-head">Number pool · ${pool.length} number${pool.length === 1 ? "" : "s"}</div>
        <div class="atn-pool-grid">
          ${pool
            .map((p) => {
              const used = (p.assignedTo || []).length;
              const who = (p.assignedTo || []).map((a) => esc(a.label || a.retailerId)).join(", ");
              return `
                <div class="atn-chip ${used ? "is-used" : "is-free"}">
                  <span class="atn-chip-num">${esc(p.number)}</span>
                  <span class="atn-chip-meta">${used ? `${used} assigned${who ? ` · ${who}` : ""}` : "free"}</span>
                </div>`;
            })
            .join("")}
        </div>`;
    }

    function visibleRetailers(): RetailerRow[] {
      const q = search.trim().toLowerCase();
      const rows = q
        ? retailers.filter(
            (r) => r.businessName.toLowerCase().includes(q) || r.id.toLowerCase().includes(q),
          )
        : retailers;
      // Allocated first, then alphabetical — keeps the meaningful rows on top.
      return [...rows].sort((a, b) => {
        if (!!a.number !== !!b.number) return a.number ? -1 : 1;
        return a.businessName.localeCompare(b.businessName);
      });
    }

    function renderList(): void {
      if (loading) {
        listEl.innerHTML = `<div class="adm-loading">Loading retailers…</div>`;
        return;
      }
      if (error) {
        listEl.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      const rows = visibleRetailers();
      if (rows.length === 0) {
        listEl.innerHTML = `<div class="adm-empty">No retailers match “${esc(search)}”.</div>`;
        return;
      }
      listEl.innerHTML = `
        <table class="atn-table">
          <thead>
            <tr><th>Retailer</th><th>Allocated number</th><th class="atn-assign-col">Assign</th></tr>
          </thead>
          <tbody>
            ${rows
              .map((r) => {
                const busy = busyId === r.id;
                return `
                <tr data-id="${esc(r.id)}">
                  <td>
                    <div class="atn-name">${esc(r.businessName)}</div>
                    <div class="atn-sub">${esc(r.id)}</div>
                  </td>
                  <td>${r.number ? `<span class="atn-num">${esc(r.number)}</span>` : `<span class="atn-none">— none —</span>`}</td>
                  <td class="atn-assign-col">
                    <div class="atn-assign">
                      <select class="adm-select atn-select" data-role="num" ${busy ? "disabled" : ""} aria-label="Choose number for ${esc(r.businessName)}">
                        ${poolOptions(r.number)}
                      </select>
                      <button class="adm-btn atn-assign-btn" type="button" data-role="assign" ${busy ? "disabled" : ""}>
                        ${busy ? "Saving…" : r.number ? "Update" : "Assign"}
                      </button>
                    </div>
                  </td>
                </tr>`;
              })
              .join("")}
          </tbody>
        </table>`;
    }

    function render(): void {
      renderPool();
      renderList();
    }

    /** Reverse-map the pool's assignedTo lists into retailerId → number. */
    function numberForRetailer(rid: string): string {
      for (const p of pool) {
        if ((p.assignedTo || []).some((a) => a.retailerId === rid)) return p.number;
      }
      return "";
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      render();
      try {
        const [numbersRes, retailersRaw] = await Promise.all([
          ctx.api<NumbersResponse>("/calls/numbers", { signal: ac.signal }),
          ctx.api<ServerRetailer[]>("/admin/retailers", { signal: ac.signal }),
        ]);
        pool = Array.isArray(numbersRes?.pool) ? numbersRes.pool : [];
        const list = Array.isArray(retailersRaw) ? retailersRaw : [];
        retailers = list.map((r) => {
          const id = String(r.id ?? "");
          return {
            id,
            businessName: String(r.businessName ?? id ?? "Unnamed retailer"),
            status: String(r.status ?? ""),
            number: numberForRetailer(id),
          };
        });
        loading = false;
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        error =
          err instanceof ApiError
            ? `Could not load phone numbers (HTTP ${err.status}).`
            : err instanceof Error
            ? err.message
            : "Could not load phone numbers.";
        render();
      }
    }

    async function assign(rid: string, platformNumber: string): Promise<void> {
      busyId = rid;
      render();
      try {
        await ctx.api<{ ok: boolean }>("/calls/assign", {
          method: "POST",
          body: JSON.stringify(platformNumber ? { retailerId: rid, platformNumber } : { retailerId: rid }),
          signal: ac.signal,
        });
        const r = retailers.find((x) => x.id === rid);
        ctx.showToast(
          `Number ${platformNumber ? "set" : "auto-allocated"} for ${r ? r.businessName : rid}.`,
          "success",
        );
        busyId = null;
        await load(); // refresh pool usage + the row's allocated number
      } catch (err) {
        if (isAbortError(err)) return;
        busyId = null;
        const msg =
          err instanceof ApiError
            ? `Assign failed (HTTP ${err.status}).`
            : err instanceof Error
            ? err.message
            : "Assign failed.";
        ctx.showToast(msg, "error");
        render();
      }
    }

    // ── Events ────────────────────────────────────────────────────────────────
    const onSearch = (): void => {
      search = searchEl.value;
      renderList();
    };
    searchEl.addEventListener("input", onSearch);

    const onRefresh = (): void => {
      void load();
    };
    refreshBtn.addEventListener("click", onRefresh);

    const onClick = (ev: MouseEvent): void => {
      const target = ev.target as HTMLElement;
      const btn = target.closest<HTMLButtonElement>("[data-role='assign']");
      if (!btn) return;
      const tr = btn.closest<HTMLTableRowElement>("tr[data-id]");
      if (!tr) return;
      const rid = tr.getAttribute("data-id") || "";
      const sel = tr.querySelector<HTMLSelectElement>("[data-role='num']");
      if (!rid || !sel) return;
      void assign(rid, sel.value);
    };
    listEl.addEventListener("click", onClick);

    void load();

    return () => {
      ac.abort();
      searchEl.removeEventListener("input", onSearch);
      refreshBtn.removeEventListener("click", onRefresh);
      listEl.removeEventListener("click", onClick);
      root.remove();
    };
  },
};

registerPage(phoneNumbersPage);
